import React, {
  ChangeEvent,
  ReactNode,
  useContext,
  forwardRef,
  ReactElement,
  Ref,
} from "react";
import { ACTION_TYPE, JobBuilderContext } from "context/jobBuilderContext";
import { TransitionProps } from "@mui/material/transitions";
import {
  Button,
  Dialog,
  Slide,
  Stack,
  TextField,
  Box,
  Card,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  mobileCardSx,
  cardHeaderSx,
  cardSx,
  AUTURA_ASPHALT_LIGHT,
} from "utils";
import { useTranslation } from "react-i18next";
import { Cancel, Warning } from "@mui/icons-material";

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement<any, any>;
  },
  ref: Ref<unknown>,
) {
  return <Slide direction="left" ref={ref} {...props} />;
});
interface dntReasonDialogProps {
  open: boolean;
  reason: string;
  dialogMessage: string;
  setOpen: (value: React.SetStateAction<boolean>) => void;
  handleResubmit: () => void;
  setReason: (value: React.SetStateAction<string>) => void;
}

const DntDialogReason = ({
  open,
  setOpen,
  setReason,
  reason,
  handleResubmit,
  dialogMessage,
}: dntReasonDialogProps): ReactNode => {
  const { t } = useTranslation();
  const { palette, breakpoints } = useTheme();
  const mobile = useMediaQuery(breakpoints.down("lg"));
  const { state, dispatch } = useContext(JobBuilderContext);
  const handleCancel = () => {
    setOpen(false);
  };

  const handleSubmit = () => {
    dispatch({
      type: ACTION_TYPE.SET_NOTES,
      payload: `${state.notes} *** DNT OVERRIDE!! reason: ${reason} ***`,
    });
    
    handleResubmit();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setReason(e.target.value.toUpperCase());
  };

  const headerText = t("dialogs.do_not_tow.title", { ns: "jobs", type: t(`jobType.${state.jobType}`, { ns: "enums" }) });

  return (
      <Dialog
        open={open}
        TransitionComponent={Transition}
        scroll={"body"}
        fullWidth
        aria-label={headerText}
        maxWidth={"sm"}
        fullScreen={mobile}
        sx={{ mt: { xs: -1, lg: 0 }, pb: 0 }}
      >
        <Card raised sx={mobile ? mobileCardSx : cardSx}>
          <Box
            sx={
              mobile
                ? {
                    display: "flex",
                    p: { xs: 2, sm: 4 },
                    backgroundColor: palette.secondary.main,
                  }
                : cardHeaderSx
            }
          >
            <Box sx={{ flex: 1, p: 0.5 }}>
              <Typography
                variant={"h4"}
                textTransform={"capitalize"}
                gutterBottom
                sx={{
                  color: palette.secondary.contrastText,
                  textAlign: "left",
                }}
              >
                {headerText}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ px: { xs: 1, sm: 4 }, pt: 4, pb: { xs: 4, lg: 4 } }}>
            <Typography variant="h6" align="center">
              {dialogMessage}
            </Typography>
            <TextField
              autoFocus
              required
              margin="none"
              id="match"
              label={t("dialogs.do_not_tow.reason", { ns: "jobs" })}
              type="text"
              fullWidth
              variant="outlined"
              onChange={handleChange}
            />
          </Box>
          <Stack
            direction="row"
            justifyContent={{ xs: "space-between", lg: "flex-end" }}
            spacing={2}
            sx={{
              px: { xs: 1, lg: 4 },
              pb: { xs: 2, lg: 4 },
              pt: { xs: 2, sm: 3, lg: 0 },
              backgroundColor: { xs: AUTURA_ASPHALT_LIGHT, lg: "inherit" },
            }}
          >
            <Button
              onClick={handleCancel}
              fullWidth
              color="error"
              startIcon={<Cancel />}
              variant={mobile ? "contained" : "outlined"}
              sx={{ m: { xs: 0, lg: 2 }, py: { xs: 1.75, lg: 0 }, flex: 1 }}
            >
              {t("Cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              fullWidth
              color="warning"
              variant={"contained"}
              disabled={!reason}
              sx={{ m: { xs: 0, lg: 2 }, flex: 1 }}
              endIcon={<Warning />}
            >
              {t("dialogs.do_not_tow.override", { ns: "jobs" })}
            </Button>
          </Stack>
        </Card>
      </Dialog>
  );
};
export default DntDialogReason;
